import Vue from 'vue'
import VueRouter from 'vue-router'
import AppLayout from '../AppLayout.vue'
import HomePage from '../pages/HomePage.vue'
import PracticePage from '../pages/PracticePage.vue'
import DiagnoseStartPage from '../pages/DiagnoseStartPage.vue'
import ProfilePage from '../pages/ProfilePage.vue'
import StudyDetailView from '../components/study/StudyDetailView.vue'
import DiagnosticResultView from '../components/diagnose/DiagnosticResultView.vue'
import DiagnoseResultView from '../components/diagnose/DiagnoseResultView.vue'
import HistoryView from '../components/user/HistoryView.vue'
import ExpertProfileView from '../components/expert/ExpertProfileView.vue'
import ExpertCaseDetail from '../components/expert/ExpertCaseDetail.vue'
import AdminLayout from '../pages/admin/AdminLayout.vue'
import DashboardPage from '../pages/admin/DashboardPage.vue'
import ExpertManager from '../pages/admin/ExpertManager.vue'
import ExpertEditor from '../pages/admin/ExpertEditor.vue'
import AuditLog from '../pages/admin/AuditLog.vue'
import ConfigManager from '../pages/admin/ConfigManager.vue'
import ContributionManager from '../pages/admin/ContributionManager.vue'
import TopicManager from '../pages/admin/TopicManager.vue'
import CaseManager from '../pages/admin/CaseManager.vue'
import CaseEditor from '../pages/admin/CaseEditor.vue'
import ScenarioManager from '../pages/admin/ScenarioManager.vue'
import DiagnosisTemplateManager from '../pages/admin/DiagnosisTemplateManager.vue'
import MatchingRules from '../pages/admin/MatchingRules.vue'
import UserManager from '../pages/admin/UserManager.vue'
import PermissionManager from '../pages/admin/PermissionManager.vue'
import LoginPage from '../pages/LoginPage.vue'
import { TOPICS, EXPERTS, EXPERT_CASES, PRESCRIPTION_DATA } from '../data'
import store from '../store'
import { ROLES, MENU_PERMISSIONS, findFirstPermittedMenu, normalizeRole } from '../data/adminConfig'

Vue.use(VueRouter)

function getDiagnosticPrescription(topic, state) {
  return {
    truth: `### 研判真相：${state.diagnosticContext?.intentStage || ''}\n\n**核心风险：** ${state.diagnosticContext?.riskAssessment || ''}\n\n**干预进度：** ${state.diagnosticContext?.interventionProgress || ''}\n\n**补充细节：** ${state.diagnosticContext?.details || '无'}\n\n--- \n\n基于您的团队处于 **${state.context.businessStage}** 且压力指数为 **${state.context.pressure}**，AI 管理能力提升助手建议：\n\n1. **立即对齐利益**：针对${state.diagnosticContext?.riskAssessment || ''}，需在24小时内开启非正式面谈。\n2. **情绪缓冲**：考虑到${state.diagnosticContext?.interventionProgress || ''}，建议引入第三方中立视角。`,
    summary: '基于诊断结果生成个性化建议',
    script: { opening: '“我们来聊聊这件事...”', responses: ['正在思考...'], closing: '“希望这能帮到您。”' },
    redLines: ['避免情绪化决策', '避免单方面施压']
  }
}

function getCustomPrescription(state) {
  return {
    truth: state.aiFeedback.split('\n\n')[0] || '正在剖析真相...',
    summary: state.aiFeedback.split('\n\n')[1] || '生成建议中...',
    script: { opening: '“我们来聊聊这件事...”', responses: ['正在生成话术...'], closing: '“按此执行即可。”' },
    redLines: ['正在划定红线...']
  }
}

function requireAdmin(to, from, next) {
  const normalizedRole = normalizeRole(store.state.userRole)

  // 普通用户不能进后台
  if (normalizedRole === ROLES.USER) {
    next('/')
    return
  }

  const currentPermissions = store.getters.currentPermissions
  const routePath = to.path.replace('/admin/', '').replace('/admin', '')

  // 根路径 /admin，重定向到第一个有权限的菜单
  if (!routePath || routePath === '') {
    const firstPermitted = findFirstPermittedMenu(currentPermissions)
    if (firstPermitted) {
      next(`/admin/${firstPermitted}`)
    } else {
      next('/')
    }
    return
  }

  // 检查具体菜单权限
  const requiredPerm = MENU_PERMISSIONS[routePath]
  if (requiredPerm && !currentPermissions.includes(requiredPerm)) {
    // 没权限，重定向到第一个有权限的页面
    const firstPermitted = findFirstPermittedMenu(currentPermissions)
    if (firstPermitted) {
      next(`/admin/${firstPermitted}`)
    } else {
      next('/')
    }
    return
  }

  next()
}

const routes = [
  { path: '/login', name: 'login', component: LoginPage },
  {
    path: '/',
    component: AppLayout,
    children: [
      { path: '', name: 'home', component: HomePage },
      { path: 'practice', name: 'practice', component: PracticePage },
      { path: 'diagnose-start', name: 'diagnose-start', component: DiagnoseStartPage },
      { path: 'diagnose-engine', redirect: '/diagnose-start' },
      { path: 'history', name: 'history', component: HistoryView },
      { path: 'profile', name: 'profile', component: ProfilePage },
      {
        path: 'topic/:id',
        name: 'topic-detail',
        component: StudyDetailView,
        props: route => {
          const id = route.params.id
          const topic = TOPICS.find(t => t.id === id)
          return {
            topic: topic || { id, title: '话题加载中...', type: '战友最痛' },
            experts: EXPERTS,
            context: store.state.context || {}
          }
        }
      },
      {
        path: 'diagnostic-result',
        name: 'diagnostic-result',
        component: DiagnosticResultView,
        props: () => {
          const state = store.state
          const topic = state.selectedTopic || { id: 'diagnostic-result', title: state.pendingQuery || '深度诊断结果', type: '战友最痛' }
          let prescription = null
          if (topic.id === 'diagnostic-result') {
            prescription = getDiagnosticPrescription(topic, state)
          } else if (topic.id === 'custom') {
            prescription = getCustomPrescription(state)
          } else {
            prescription = PRESCRIPTION_DATA[topic.id] || null
          }
          return {
            topic,
            prescription,
            experts: EXPERTS,
            context: state.context,
            diagnosticContext: state.diagnosticContext,
            isGenerating: state.isGeneratingFeedback,
            chatHistory: state.history.find(h => h.id === state.activeHistoryId)?.chatHistory || []
          }
        }
      },
      {
        path: 'diagnose-result',
        name: 'diagnose-result',
        component: DiagnoseResultView,
        props: () => ({
          query: store.state.pendingQuery,
          answers: {}
        })
      },
      {
        path: 'expert/:expertId/case/:caseId',
        name: 'expert-case-detail',
        component: ExpertCaseDetail,
        props: route => {
          const { caseId } = route.params
          const expertCase = EXPERT_CASES[caseId]
          const state = store.state
          return {
            expertCase: expertCase || { id: caseId, title: '案例加载中...', content: '', tags: [], expertId: route.params.expertId, expertName: '' },
            autoFocusMedia: route.query.type,
            initialIsBookmarked: state.userStats.bookmarks?.includes(caseId),
            initialIsLiked: state.userStats.likes?.includes(caseId)
          }
        }
      },
      {
        path: 'expert/:expertId',
        name: 'expert-detail',
        component: ExpertProfileView,
        props: route => {
          const expert = EXPERTS.find(e => e.id === route.params.expertId)
          return {
            expert: expert || EXPERTS[0],
            onBack: () => {},
            onBook: () => {},
            onViewCase: () => {}
          }
        }
      }
    ]
  },
  {
    path: '/admin',
    component: AdminLayout,
    beforeEnter: requireAdmin,
    children: [
      { path: '', component: DashboardPage },
      { path: 'experts', component: ExpertManager },
      { path: 'experts/edit', component: ExpertEditor },
      { path: 'contributions', component: ContributionManager },
      { path: 'topics', component: TopicManager },
      { path: 'cases', component: CaseManager },
      { path: 'cases/edit', component: CaseEditor },
      { path: 'scenarios', component: ScenarioManager },
      { path: 'diagnosis-templates', component: DiagnosisTemplateManager },
      { path: 'matching-rules', component: MatchingRules },
      { path: 'users', component: UserManager },
      { path: 'permissions', component: PermissionManager },
      { path: 'config', component: ConfigManager },
      { path: 'audit', component: AuditLog },
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

// 登录守卫：未登录用户只能访问登录页
router.beforeEach((to, from, next) => {
  const isLoggedIn = store.state.isLoggedIn

  if (to.path === '/login') {
    if (isLoggedIn) {
      const role = store.state.userRole
      if (role === ROLES.USER) {
        next('/')
      } else {
        next('/admin')
      }
    } else {
      next()
    }
    return
  }

  if (!isLoggedIn) {
    next('/login')
    return
  }

  next()
})

export default router
